import { signalStore, withState, withMethods, patchState, withHooks } from '@ngrx/signals';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

export interface User {
    id: string;
    userName: string;
    name: string;
    active: boolean;
    roles: string | string[];
}

type AuthState = {
    isAuthenticated: boolean;
    user: User | null;
    loading: boolean;
    error: string | null;
};

const initialState: AuthState = {
    isAuthenticated: false,
    user: null,
    loading: false,
    error: null,
};

function decodeToken(token: string): any {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
}

function isTokenExpired(token: string): boolean {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    return Math.floor(new Date().getTime() / 1000) >= decoded.exp;
}

export function hasRole(user: User | null, roleToFind: string): boolean {
    const roles = user?.roles;
    const normalizedRole = roleToFind.trim().toLowerCase();

    if (Array.isArray(roles)) {
        return roles.some((role) => String(role ?? '').trim().toLowerCase() === normalizedRole);
    }

    return String(roles ?? '').trim().toLowerCase() === normalizedRole;
}

export function isMemberUser(user: User | null): boolean {
    return hasRole(user, 'member');
}

export const AuthStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((store, router = inject(Router), http = inject(HttpClient)) => ({
        async login(credentials: any) {
            patchState(store, { loading: true, error: null });

            const headers = new HttpHeaders({
                'accept': '*/*',
                'X-TenantId': environment.tenantId,
                'Content-Type': 'application/json'
            });

            const body = {
                userName: credentials.userName,
                password: credentials.password
            };

            try {
                const response: any = await firstValueFrom(
                    http.post(environment.urlApiLogin, body, { headers })
                );

                if (response && response.token) {
                    localStorage.setItem('token', response.token);
                    const decoded = decodeToken(response.token);

                    let user: User | null = null;
                    if (decoded) {
                        user = {
                            id: decoded.id,
                            userName: decoded.unique_name,
                            name: `${decoded.name || ''} ${decoded.family_name || ''}`.trim(),
                            active: true,
                            roles: decoded.roles
                        };
                        localStorage.setItem('currentUser', JSON.stringify(user));
                    }

                    patchState(store, {
                        isAuthenticated: true,
                        user,
                        loading: false
                    });

                    router.navigate([isMemberUser(user) ? '/sales-report' : '/']);
                } else {
                    throw new Error(response?.message || 'Error de autenticación: No se recibió token');
                }
            } catch (err: any) {
                console.error('Error al autenticar:', err);
                patchState(store, {
                    loading: false,
                    error: err?.error?.message || err?.message || 'Error al intentar autenticar. Por favor, intente de nuevo.'
                });
            }
        },
        logout() {
            localStorage.removeItem('token');
            localStorage.removeItem('currentUser');
            patchState(store, initialState);
            router.navigate(['/auth/login']);
        }
    })),
    withHooks({
        onInit(store) {
            const token = localStorage.getItem('token');
            const currentUserStr = localStorage.getItem('currentUser');

            if (token && currentUserStr && !isTokenExpired(token)) {
                patchState(store, { isAuthenticated: true, user: JSON.parse(currentUserStr), loading: false });
            } else {
                localStorage.removeItem('token');
                localStorage.removeItem('currentUser');
                patchState(store, initialState);
            }
        }
    })
);
